<script setup lang="ts">
import type { Referenz } from '~/types/referenzen/Referenz'

const properties = defineProps({
  refs: {
    type: Array as () => Referenz[],
    required: true
  }
})

const orderFromIndex = (index: number) => {
  const itemsPerRow = 2
  const itemsPerRowLg = 4

  const row = Math.floor((index * 2) / itemsPerRow)
  const rowLg = Math.floor((index * 2) / itemsPerRowLg)

  const isEven = row % 2 === 0 || row === 0
  const isEvenLg = rowLg % 2 === 0 || rowLg === 0

  return {
    index,
    image: {
      order: isEven ? index * 2 : index * 2 + 1,
      orderLg: String(isEvenLg ? index * 2 : index * 2 + 1)
    },
    text: {
      order: isEven ? index * 2 + 1 : index * 2,
      orderLg: String(isEvenLg ? index * 2 + 1 : index * 2)
    },
    triangle: `triangle-shape-${isEven ? 'right' : 'left'} ${isEvenLg ? 'triangle-lg-shape-right' : 'triangle-lg-shape-left'}`
  }
}
</script>

<template>
  <v-row class="fill-height" no-gutters>
    <template v-for="(ref, index) in properties.refs" :key="ref.title">
      <v-col
        cols="6"
        lg="3"
        :order="orderFromIndex(index).image.order"
        :order-lg="orderFromIndex(index).image.orderLg"
      >
        <div class="shape-container">
          <v-carousel
            cycle
            height="auto"
            hide-delimiter-background
            :show-arrows="false"
            :aria-label="`Bildergalerie: ${ref.title}`"
          >
            <v-carousel-item v-for="(image, i) in ref.images" :key="i">
              <NuxtImg
                :src="image"
                :alt="ref.title"
                width="600"
                height="600"
                fit="cover"
                loading="lazy"
                class="carousel-image"
              />
            </v-carousel-item>
          </v-carousel>
          <div :class="orderFromIndex(index).triangle" aria-hidden="true"></div>
        </div>
      </v-col>
      <v-col
        cols="6"
        lg="3"
        :order="orderFromIndex(index).text.order"
        :order-lg="orderFromIndex(index).text.orderLg"
        class="px-6 px-lg-8 py-1 py-lg-4"
      >
        <p class="text-primary ref-title font-weight-regular mt-1 mb-1 mb-lg-2">{{ ref.title }}</p>
        <p class="text-caption text-lg-h5 mb-2 mb-lg-6">
          <span class="font-weight-regular">{{ ref.subtitle }}</span>
        </p>
        <div>
          <p class="text-caption">
            <span class="font-weight-regular">Auftraggeber:</span> {{ ref.client }}
          </p>
          <p class="text-caption"><span class="font-weight-regular">Ort:</span> {{ ref.city }}</p>
          <p class="text-caption">
            <span class="font-weight-regular">Objektgröße:</span> {{ ref.size }}
          </p>
          <p class="text-caption">
            <span class="font-weight-regular">Bauzeit:</span> {{ ref.year }}
          </p>
          <p class="text-caption">
            <span class="font-weight-regular">Leistung:</span> {{ ref.service }}
          </p>
        </div>
      </v-col>
    </template>
  </v-row>
</template>

<style scoped lang="scss">
.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.zoom-on-hover {
  transition: transform 0.5s;
}

.zoom-on-hover:hover {
  transform: scale(1.1);
}

.shape-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.triangle-shape-right {
  border-left: 5vw solid transparent;
  border-right: 5vw solid #fff;
  border-bottom: 5vw solid transparent;
  border-top: 5vw solid transparent;
  display: inline-block;
  position: absolute;
  top: 50%;
  right: 0;
  z-index: 1;
  transform: translate(0, -50%);
}

.triangle-shape-left {
  border-left: 5vw solid #fff;
  border-right: 5vw solid transparent;
  border-bottom: 5vw solid transparent;
  border-top: 5vw solid transparent;
  display: inline-block;
  position: absolute;
  top: 50%;
  left: 0;
  z-index: 1;
  transform: translate(0, -50%);
}

@media (min-width: 1264px) {
  .triangle-lg-shape-right {
    border-left: 2.5vw solid transparent;
    border-right: 2.5vw solid #fff;
    border-bottom: 2.5vw solid transparent;
    border-top: 2.5vw solid transparent;
    display: inline-block;
    position: absolute;
    top: 50%;
    right: 0;
    z-index: 1;
    transform: translate(0, -50%);
  }
  .triangle-lg-shape-left {
    border-left: 2.5vw solid #fff;
    border-right: 2.5vw solid transparent;
    border-bottom: 2.5vw solid transparent;
    border-top: 2.5vw solid transparent;
    display: inline-block;
    position: absolute;
    top: 50%;
    left: 0;
    z-index: 1;
    transform: translate(0, -50%);
  }
}
</style>
